import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOverFooter, setIsOverFooter] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const location = useLocation();
  
  // Initialize component
  useEffect(() => {
    setIsLoaded(true);
    
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Set active section based on current route
  useEffect(() => {
    if (location.pathname === '/quote') {
      setActiveSection('quote');
    } else if (location.pathname === '/') {
      setActiveSection('home');
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      // Update scroll state for header effects
      const scrolled = window.scrollY > 50;
      setIsScrolled(scrolled);
      
      // Check if header is over footer
      const footer = document.querySelector('footer');
      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        const headerHeight = 120; // Header height
        const isOverFooterArea = footerRect.top <= headerHeight;
        setIsOverFooter(isOverFooterArea);
      }
      
      // Only update active section on home page
      if (location.pathname === '/') {
        const sections = ['home', 'about', 'services', 'portfolio', 'contact'];
        const currentSection = sections.find(section => {
          const element = document.getElementById(section);
          if (element) {
            const rect = element.getBoundingClientRect();
            return rect.top <= 100 && rect.bottom >= 100;
          }
          return false;
        });
        if (currentSection) {
          setActiveSection(currentSection);
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const navItems = [
    { href: '/', label: 'Home', id: 'home', icon: '🏠' },
    { href: '/#about', label: 'About', id: 'about', icon: '✈️' },
    { href: '/#services', label: 'Services', id: 'services', icon: '🎯' },
    { href: '/#portfolio', label: 'Portfolio', id: 'portfolio', icon: '💼' },
    { href: '/#contact', label: 'Contact', id: 'contact', icon: '📞' }
  ];

  // Smooth scroll function
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 100; // Account for fixed header
      const elementPosition = element.offsetTop - headerHeight;
      
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  // Handle navigation click
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, item: any) => {
    e.preventDefault();
    
    if (item.href === '/') {
      // If it's home, navigate to home page
      if (location.pathname !== '/') {
        window.location.href = '/';
      } else {
        // If already on home page, scroll to top
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
      setActiveSection('home');
    } else if (item.href.startsWith('/#')) {
      // If it's a section link, scroll to that section
      const sectionId = item.href.substring(2); // Remove '/#'
      scrollToSection(sectionId);
      setActiveSection(sectionId);
    }
    
    // Close mobile menu if open
    setIsMenuOpen(false);
  };

  return (
    <>
      {/* Enhanced Dynamic Background Elements */}
      <div className="fixed top-0 left-0 right-0 h-24 pointer-events-none z-40">
        {/* Floating particles */}
        <div className="absolute top-6 left-1/4 w-3 h-3 bg-gradient-to-r from-brand-orange/30 to-brand-gold/30 rounded-full animate-pulse blur-sm"></div>
        <div className="absolute top-10 right-1/3 w-2 h-2 bg-gradient-to-r from-brand-gold/40 to-brand-orange/40 rounded-full animate-ping blur-sm"></div>
        <div className="absolute top-14 left-3/4 w-2.5 h-2.5 bg-gradient-to-r from-brand-orange/25 to-brand-gold/25 rounded-full animate-bounce blur-sm"></div>
        <div className="absolute top-8 right-1/4 w-1.5 h-1.5 bg-gradient-to-r from-brand-gold/35 to-brand-orange/35 rounded-full animate-pulse blur-sm"></div>
        <div className="absolute top-12 left-1/2 w-2 h-2 bg-gradient-to-r from-brand-orange/20 to-brand-gold/20 rounded-full animate-ping blur-sm"></div>
        <div className="absolute top-16 right-1/5 w-1 h-1 bg-gradient-to-r from-brand-gold/30 to-brand-orange/30 rounded-full animate-bounce blur-sm"></div>
      </div>

      {/* Interactive Mouse Follower */}
      <div 
        className="fixed w-32 h-32 bg-gradient-to-r from-brand-orange/10 via-brand-gold/10 to-brand-orange/10 rounded-full blur-2xl pointer-events-none transition-all duration-1000 ease-out z-30"
        style={{
          left: mousePosition.x - 64,
          top: mousePosition.y - 64,
        }}
      ></div>

      <header 
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'bg-white shadow-2xl shadow-brand-orange/10 border-b border-brand-orange/20' 
            : 'bg-white shadow-lg border-b border-gray-200/50'
        }`}
      >
        
        {/* Dynamic Border Glow */}
        <div className={`absolute bottom-0 left-0 right-0 h-px transition-all duration-500 ${
          isScrolled 
            ? 'bg-gradient-to-r from-transparent via-brand-orange/50 to-transparent' 
            : 'bg-gradient-to-r from-transparent via-brand-orange/30 to-transparent'
        }`}></div>
        
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex justify-between items-center py-5 lg:py-6" style={{ height: '120px' }}>
            {/* Clean Logo Design */}
            <div 
              className="flex items-center justify-center group cursor-pointer mt-2"
              onClick={() => {
                if (location.pathname !== '/') {
                  window.location.href = '/';
                } else {
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                  });
                }
                setActiveSection('home');
              }}
            >
                  <img 
                    src="/assets/BA Logo-02 (1).png" 
                    alt="Brand Aviation" 
                    className="h-[150px] w-[150px] transition-all duration-300 group-hover:scale-105 group-hover:brightness-110"
                    onError={(e) => {
                      console.log('Logo failed to load, trying alternative');
                      e.currentTarget.src = '/assets/BA Logo-01 (1).png';
                    }}
                  />
            </div>

            {/* Ultra-Modern Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navItems.map((item, index) => (
                <a 
                  key={item.id}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item)}
                  className={`relative px-5 py-3 text-sm font-semibold transition-all duration-500 group rounded-xl cursor-pointer overflow-hidden ${
                    activeSection === item.id
                      ? 'text-white bg-gradient-to-r from-brand-orange to-brand-gold shadow-lg shadow-brand-orange/25'
                      : 'text-gray-700 hover:text-white hover:bg-gradient-to-r hover:from-brand-orange/80 hover:to-brand-gold/80 hover:shadow-md hover:shadow-brand-orange/20'
                  } ${isLoaded ? 'animate-fade-in-up' : ''}`}
                  style={{ animationDelay: `${0.2 + index * 0.1}s` }}
                >
                  {/* Advanced Background */}
                  <div className={`absolute inset-0 transition-all duration-500 ${
                    activeSection === item.id
                      ? 'bg-gradient-to-r from-brand-orange to-brand-gold'
                      : 'bg-transparent group-hover:bg-gradient-to-r group-hover:from-brand-orange/80 group-hover:to-brand-gold/80'
                  }`}></div>
                  
                  {/* Shimmer Effect */}
                  <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                  
                  {/* Icon and Text */}
                  <span className="relative z-10 flex items-center space-x-3">
                    <span className={`text-lg transition-all duration-500 group-hover:scale-125 group-hover:rotate-12 ${
                      activeSection === item.id ? 'animate-pulse' : ''
                    }`}>
                      {item.icon}
                    </span>
                    <span className="font-medium">{item.label}</span>
                  </span>
                  
                  {/* Enhanced Underline */}
                  <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 h-1 transition-all duration-500 rounded-full ${
                    activeSection === item.id
                      ? 'w-full bg-white/80'
                      : 'w-0 bg-white/80 group-hover:w-full'
                  }`}></div>
                  
                  {/* Floating Elements */}
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-white/60 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                  <div className="absolute -bottom-1 -left-1 w-1.5 h-1.5 bg-white/80 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-bounce transition-all duration-700 delay-100"></div>
                </a>
              ))}
            </nav>

            {/* Ultra-Modern CTA Button */}
            <div className="hidden lg:block">
              <Link to="/quote" className={`relative group inline-block ${isLoaded ? 'animate-fade-in-up' : ''}`} style={{ animationDelay: '0.8s' }}>
                {/* Enhanced Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-brand-orange/30 to-brand-gold/30 rounded-2xl blur-2xl opacity-0 group-hover:opacity-100 transition-all duration-700 -z-10 scale-125"></div>
                
                {/* Advanced Button */}
                <div className="relative bg-gradient-to-r from-brand-orange via-brand-gold to-brand-orange text-white px-8 py-4 rounded-2xl font-bold text-sm shadow-2xl shadow-brand-orange/25 group-hover:shadow-3xl group-hover:shadow-brand-orange/40 transition-all duration-500 group-hover:scale-110 group-hover:-rotate-1 overflow-hidden">
                  <span className="relative z-10 flex items-center space-x-3">
                    <span>Get Quote</span>
                    <svg className="w-5 h-5 transition-all duration-500 group-hover:translate-x-2 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                  
                  {/* Advanced Shimmer Effect */}
                  <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white/30 to-transparent transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                  
                  {/* Floating Particles */}
                  <div className="absolute -top-2 -right-2 w-3 h-3 bg-white/60 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                  <div className="absolute -bottom-2 -left-2 w-2 h-2 bg-white/80 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-bounce transition-all duration-700 delay-100"></div>
                  <div className="absolute top-1/2 -left-1 w-1 h-1 bg-white/70 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-500 delay-200"></div>
                </div>
              </Link>
            </div>

            {/* Ultra-Modern Mobile Menu Button */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`relative p-4 rounded-2xl backdrop-blur-xl border-2 shadow-2xl hover:shadow-3xl transition-all duration-500 group ${
                  isOverFooter 
                    ? 'bg-white/95 border-brand-orange/50 hover:shadow-brand-orange/30' 
                    : 'bg-white/95 border-brand-orange/30 hover:shadow-brand-orange/30'
                } ${isLoaded ? 'animate-fade-in-up' : ''}`} 
                style={{ animationDelay: '0.9s' }}
              >
                {/* Enhanced Glow Effect */}
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-500 -z-10 scale-110 bg-gradient-to-r from-brand-orange/30 to-brand-gold/30"></div>
                
                {/* Background for better visibility */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-brand-orange/10 to-brand-gold/10"></div>
                
                <div className="w-6 h-6 flex flex-col justify-center items-center relative z-10">
                  <span className={`block h-1 w-6 transition-all duration-500 rounded-full shadow-sm bg-gradient-to-r from-brand-orange to-brand-gold ${isMenuOpen ? 'rotate-45 translate-y-2' : '-translate-y-2'}`}></span>
                  <span className={`block h-1 w-6 transition-all duration-500 rounded-full shadow-sm bg-gradient-to-r from-brand-orange to-brand-gold ${isMenuOpen ? 'opacity-0 scale-0' : 'opacity-100 scale-100'}`}></span>
                  <span className={`block h-1 w-6 transition-all duration-500 rounded-full shadow-sm bg-gradient-to-r from-brand-orange to-brand-gold ${isMenuOpen ? '-rotate-45 -translate-y-2' : 'translate-y-2'}`}></span>
                </div>
                
                {/* Floating Elements */}
                <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500 bg-brand-orange/80"></div>
                <div className="absolute -bottom-1 -left-1 w-1.5 h-1.5 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-bounce transition-all duration-700 delay-100 bg-brand-gold/90"></div>
              </button>
            </div>
        </div>

          {/* Ultra-Modern Mobile Navigation */}
          <div className={`lg:hidden overflow-hidden transition-all duration-500 ${
            isMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
          }`}>
            <div className={`py-6 space-y-3 backdrop-blur-2xl rounded-2xl border shadow-2xl mt-3 transition-all duration-500 ${
              isOverFooter 
                ? 'bg-white/98 border-gray-200/60 shadow-brand-orange/10' 
                : 'bg-white/98 border-gray-200/60 shadow-brand-orange/10'
            }`}>
              {navItems.map((item, index) => (
                <a 
                  key={item.id}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item)}
                  className="block px-6 py-4 rounded-xl transition-all duration-500 font-semibold group cursor-pointer overflow-hidden text-gray-700 hover:text-white hover:bg-gradient-to-r hover:from-brand-orange/80 hover:to-brand-gold/80"
                  style={{ 
                    animationDelay: isMenuOpen ? `${index * 0.1}s` : '0s',
                    animation: isMenuOpen ? 'fadeInUp 0.5s ease-out forwards' : 'none'
                  }}
                >
                  {/* Background */}
                  <div className="absolute inset-0 transition-all duration-500 bg-transparent group-hover:bg-gradient-to-r group-hover:from-brand-orange/80 group-hover:to-brand-gold/80"></div>
                  
                  {/* Shimmer Effect */}
                  <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                  
                  <span className="relative z-10 flex items-center space-x-4">
                    <span className="text-xl transition-all duration-500 group-hover:scale-125 group-hover:rotate-12">
                      {item.icon}
                    </span>
                    <span className="font-medium text-gray-700 group-hover:text-white transition-colors duration-300">{item.label}</span>
                  </span>
                  
                  {/* Floating Elements */}
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-white/60 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                  <div className="absolute -bottom-1 -left-1 w-1.5 h-1.5 bg-white/80 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-bounce transition-all duration-700 delay-100"></div>
                </a>
              ))}
              
              <div className="px-6 pt-4">
                <Link 
                  to="/quote"
                  onClick={() => setIsMenuOpen(false)}
                  className="block w-full bg-gradient-to-r from-brand-orange via-brand-gold to-brand-orange text-white px-8 py-4 rounded-2xl hover:shadow-2xl hover:shadow-brand-orange/30 transition-all duration-500 font-bold group relative overflow-hidden"
                  style={{ 
                    animationDelay: isMenuOpen ? '0.6s' : '0s',
                    animation: isMenuOpen ? 'fadeInUp 0.5s ease-out forwards' : 'none'
                  }}
                >
                  {/* Enhanced Shimmer Effect */}
                  <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white/30 to-transparent transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                  
                  <span className="relative z-10 flex items-center justify-center space-x-3">
                    <span>Get Quote</span>
                    <svg className="w-5 h-5 transition-all duration-500 group-hover:translate-x-2 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                  
                  {/* Floating Particles */}
                  <div className="absolute -top-2 -right-2 w-3 h-3 bg-white/60 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                  <div className="absolute -bottom-2 -left-2 w-2 h-2 bg-white/80 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-bounce transition-all duration-700 delay-100"></div>
                </Link>
              </div>
            </div>
          </div>
      </div>
    </header>
    </>
  );
};

export default Header;
